import {
  fitsAtBaseResolution,
  METERS_PER_DEGREE_LATITUDE,
  metersPerDegreeLngAt,
  NO_INDEX,
} from 'app/utils/field-boundary/grid';
import { isPositiveNumber } from 'app/utils/field-boundary/shared';
import {
  FieldBoundarySuggestionOptions,
  LatLngPoint,
} from 'app/utils/field-boundary/types';

/**
 * Polygons spanning more buckets than this on an axis are corrupt data (no
 * pass runs for tens of kilometers) and are left out, which also bounds the
 * bucketing work.
 */
const MAX_POLYGON_SPAN_BUCKETS = 50;

interface Bucket {
  x: number;
  y: number;
  owner: number;
}

/** Area of a path in square meters, on a projection local to its first point. */
function planarArea(path: LatLngPoint[], metersPerDegreeLng: number): number {
  const origin = path[0];
  let doubledArea = 0;
  path.forEach((point, index) => {
    const next = path[(index + 1) % path.length];
    const x = (point.lng - origin.lng) * metersPerDegreeLng;
    const y = (point.lat - origin.lat) * METERS_PER_DEGREE_LATITUDE;
    const nextX = (next.lng - origin.lng) * metersPerDegreeLng;
    const nextY = (next.lat - origin.lat) * METERS_PER_DEGREE_LATITUDE;
    doubledArea += x * nextY - nextX * y;
  });
  return Math.abs(doubledArea) / 2;
}

/**
 * Keeps only the polygons of the main worked area when the whole task would
 * not fit the grid at full resolution.
 *
 * @remarks
 * The grid is sized by everything it rasterizes, so a patch worked in another
 * field kilometers away would coarsen the cells, and with them the precision
 * of the boundary kept, even though the joining step drops that patch later.
 * Polygons are bucketed by the join distance, those sharing a bucket or in
 * adjacent buckets are grouped, and the group with the largest area is kept.
 * Adjacent buckets reach twice the join distance, so this never separates
 * blocks that the joining step would join.
 */
export function keepMainCluster(
  polygons: LatLngPoint[][],
  settings: FieldBoundarySuggestionOptions,
): LatLngPoint[][] {
  const bucketMeters = Math.max(
    settings.maxJoinGapMeters,
    settings.gapClosingMeters,
    settings.cellSizeMeters,
  );
  if (
    polygons.length < 2 ||
    !isPositiveNumber(bucketMeters) ||
    fitsAtBaseResolution(polygons, settings)
  ) {
    return polygons;
  }

  const metersPerDegreeLng = metersPerDegreeLngAt(polygons[0][0].lat);
  const toBucketX = (lng: number) =>
    Math.floor((lng * metersPerDegreeLng) / bucketMeters);
  const toBucketY = (lat: number) =>
    Math.floor((lat * METERS_PER_DEGREE_LATITUDE) / bucketMeters);

  const parents = polygons.map((_, index) => index);
  const findGroup = (index: number): number => {
    let root = index;
    while (parents[root] !== root) {
      parents[root] = parents[parents[root]];
      root = parents[root];
    }
    return root;
  };
  const join = (first: number, second: number) => {
    const firstRoot = findGroup(first);
    const secondRoot = findGroup(second);
    if (firstRoot !== secondRoot) {
      parents[secondRoot] = firstRoot;
    }
  };

  const owners = new Map<string, Bucket>();
  const usable = polygons.map((path, index) => {
    let firstX = Infinity;
    let lastX = -Infinity;
    let firstY = Infinity;
    let lastY = -Infinity;
    path.forEach((point) => {
      firstX = Math.min(firstX, toBucketX(point.lng));
      lastX = Math.max(lastX, toBucketX(point.lng));
      firstY = Math.min(firstY, toBucketY(point.lat));
      lastY = Math.max(lastY, toBucketY(point.lat));
    });
    const isCorrupt =
      lastX - firstX > MAX_POLYGON_SPAN_BUCKETS ||
      lastY - firstY > MAX_POLYGON_SPAN_BUCKETS;
    if (!isCorrupt) {
      for (let x = firstX; x <= lastX; x++) {
        for (let y = firstY; y <= lastY; y++) {
          const key = `${x},${y}`;
          const bucket = owners.get(key);
          if (bucket) {
            join(bucket.owner, index);
          } else {
            owners.set(key, { x, y, owner: index });
          }
        }
      }
    }
    return !isCorrupt;
  });
  owners.forEach(({ x, y, owner }) => {
    for (let neighborX = x - 1; neighborX <= x + 1; neighborX++) {
      for (let neighborY = y - 1; neighborY <= y + 1; neighborY++) {
        const neighbor = owners.get(`${neighborX},${neighborY}`);
        if (neighbor) {
          join(owner, neighbor.owner);
        }
      }
    }
  });

  const groupAreas = new Map<number, number>();
  polygons.forEach((path, index) => {
    if (usable[index]) {
      const group = findGroup(index);
      groupAreas.set(
        group,
        (groupAreas.get(group) ?? 0) + planarArea(path, metersPerDegreeLng),
      );
    }
  });
  let mainGroup = NO_INDEX;
  groupAreas.forEach((groupArea, group) => {
    if (mainGroup === NO_INDEX || groupArea > groupAreas.get(mainGroup)) {
      mainGroup = group;
    }
  });

  return polygons.filter(
    (_, index) => usable[index] && findGroup(index) === mainGroup,
  );
}
